import { useEffect, useRef, useState } from "react";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, BedDouble, Bath, Maximize, Phone, Mail, ShieldCheck, CheckCircle2, Heart, Lock, LogIn, UserPlus, Eye, ChevronLeft, ChevronRight, X, Share2, MessageCircle, Send, Sparkles, Compass, FileText, Home as HomeIcon, TrendingUp, ChevronRight as ChevronRightIcon } from "lucide-react";
import { api, fileUrl, formatINR, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import Seo, { propertyJsonLd, breadcrumbJsonLd, faqJsonLd } from "../components/Seo";
import PropertyShareMenu from "../components/PropertyShareMenu";
import PropertyCard from "../components/PropertyCard";
import NearbyPOIs from "../components/NearbyPOIs";
import ChatButton from "../components/ChatButton";
import { trackMeta } from "../lib/metaPixel";
import PropertyMedia from "../components/PropertyMedia";
import ScheduleVisitForm from "../components/ScheduleVisitForm";
import { profileAvatar } from "../lib/avatar";
import { useRecentlyViewed } from "../hooks/useRecentlyViewed";

export default function PropertyDetail() {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const refCode = searchParams.get("ref");
    const [p, setP] = useState(null);
    const [active, setActive] = useState(0);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [descExpanded, setDescExpanded] = useState(false);
    const [agent, setAgent] = useState(null);
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const [similar, setSimilar] = useState([]);
    const { user } = useAuth();
    const { push: pushRecentlyViewed } = useRecentlyViewed();

    useEffect(() => {
        api.get(`/properties/${id}`).then((r) => {
            setP(r.data);
            pushRecentlyViewed(r.data);
            // Fire Meta ViewContent for retargeting
            try {
                trackMeta("ViewContent", {
                    content_ids: [r.data.id],
                    content_name: r.data.title,
                    content_type: "property",
                    content_category: r.data.property_type,
                    value: r.data.price,
                    currency: "INR",
                });
            } catch { /* silent */ }
            // Only fetch partner profile if the owner is actually a partner (broker / advocate / loan_agent / architect / interior_designer / civil_contractor / admin)
            const partnerRoles = ["broker", "advocate", "loan_agent", "architect", "interior_designer", "civil_contractor", "admin"];
            if (r.data.owner_id && partnerRoles.includes(r.data.owner_role)) {
                api.get(`/partners/${r.data.owner_id}`).then((r2) => setAgent(r2.data)).catch(() => {});
            }
        }).catch(() => setP(false));
        // Fire-and-forget similar properties fetch
        api.get(`/properties/${id}/similar`, { params: { limit: 6 } })
            .then((r) => setSimilar(r.data?.items || []))
            .catch(() => setSimilar([]));
    }, [id, pushRecentlyViewed]);

    // Track property-referral click (anonymous, fire-and-forget) — backend silently rejects non-eligible
    useEffect(() => {
        if (!refCode || !id || !p || p.referral_commission_eligible === false) return;
        const src = document.referrer.includes("whatsapp") ? "whatsapp"
            : document.referrer.includes("facebook") ? "facebook"
            : document.referrer.includes("t.me") ? "telegram"
            : document.referrer.includes("instagram") ? "instagram"
            : "direct";
        api.post("/referrals/property-click", { property_id: id, ref_code: refCode, source: src }).catch(() => {});
    }, [refCode, id, p]);

    useEffect(() => {
        if (user) setForm((f) => ({ ...f, name: user.name || "", email: user.email || "", phone: user.phone || "" }));
    }, [user]);

    if (p === null) {
        return <div className="min-h-screen flex items-center justify-center"><div className="text-gold-500">Loading…</div></div>;
    }
    if (p === false) {
        return (
            <div className="min-h-screen flex items-center justify-center text-center">
                <div>
                    <h2 className="text-2xl font-heading font-bold text-white">Property not found</h2>
                    <Link to="/properties" className="text-gold-500 mt-4 inline-block">← Back to listings</Link>
                </div>
            </div>
        );
    }

    // Login wall: anonymous visitors see a premium gate instead of the property details.
    // (SEO meta tags are still rendered inside <Seo /> so crawlers continue to index the page.)
    if (!user) {
        return <LoginWall property={p} />;
    }

    const images = p.images?.length ? p.images : ["https://images.unsplash.com/photo-1638454795595-0a0abf68614d?crop=entropy&cs=srgb&fm=jpg&q=85"];

    const onSave = async () => {
        if (!user) { toast.error("Please log in to save properties"); return; }
        try {
            const { data } = await api.post(`/saved/${p.id}`);
            toast.success(data.saved ? "Saved!" : "Removed");
        } catch (e) { toast.error("Failed"); }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/inquiries", { ...form, property_id: p.id, ref_code: refCode || null });
            try {
                trackMeta("Lead",
                    { content_ids: [p.id], content_name: p.title, content_type: "property", value: p.price, currency: "INR" },
                    { email: form?.email, phone: form?.phone }
                );
            } catch { /* silent */ }
            toast.success("Inquiry sent! We'll get back within 24 hours.");
            setForm({ name: user?.name || "", email: user?.email || "", phone: user?.phone || "", message: "" });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    const lat = p.latitude || 21.1458, lon = p.longitude || 79.0882;
    const mapSrc = `https://www.openstreetmap.org/export/embed.html?bbox=${lon-0.02}%2C${lat-0.012}%2C${lon+0.02}%2C${lat+0.012}&layer=mapnik&marker=${lat}%2C${lon}`;

    // Build SEO-optimized title with location keywords for organic search
    const seoAction = p.listing_type === "rent" ? "for Rent" : "for Sale";
    const seoTitle = (() => {
        const bhkPart = p.bhk ? `${p.bhk} BHK ` : "";
        const typePart = (p.subtype || p.property_type || "").replace(/_/g, " ");
        const placePart = [p.locality, p.city, p.state].filter(Boolean).join(", ");
        return `${bhkPart}${typePart} ${seoAction} in ${placePart}`.trim();
    })();
    const seoDesc = `${seoTitle} · ${p.area_sqft ? `${p.area_sqft} sqft · ` : ""}${formatINR(p.price)}${p.listing_type === "rent" ? "/month" : ""}. ${(p.description || "").slice(0, 140)}`;
    const seoKeywords = [
        seoTitle,
        p.locality && `property in ${p.locality}`,
        p.city && `${p.property_type} in ${p.city}`,
        p.city && `plots in ${p.city}`,
        `${p.subtype || p.property_type} ${seoAction.toLowerCase()}`,
    ].filter(Boolean).join(", ");

    // FAQ JSON-LD — auto-generated Q&A for AI search engines (Google SGE, Perplexity, ChatGPT)
    const priceLabel = `${formatINR(p.price)}${p.listing_type === "rent" ? "/month" : ""}`;
    const placeLabel = [p.locality, p.city].filter(Boolean).join(", ") || p.city || "Nagpur";
    const propFaqs = [
        { q: `What is the price of ${p.title}?`, a: `The ${p.listing_type === "rent" ? "monthly rent" : "asking price"} for ${p.title} in ${placeLabel} is ${priceLabel}.` },
        p.area_sqft && { q: `What is the area of ${p.title}?`, a: `${p.title} has a built-up / carpet area of ${p.area_sqft} sqft.` },
        p.bhk && { q: `How many bedrooms does ${p.title} have?`, a: `${p.title} is a ${p.bhk} BHK ${(p.subtype || p.property_type || "property").replace(/_/g, " ")}.` },
        p.possession_status && { q: `What is the possession status of ${p.title}?`, a: `${p.title} is ${p.possession_status === "ready_to_move" ? "ready to move in" : "under construction"}${p.possession_date ? `, with possession expected by ${p.possession_date}` : ""}.` },
        { q: `Is ${p.title} RERA-verified?`, a: p.verified ? `Yes, ${p.title} is a verified listing on Global Property Mart with all documents checked by our team.` : `${p.title} is listed on Global Property Mart. Verification status is being updated — please contact the seller for RERA / legal documents.` },
        { q: `How do I schedule a site visit for ${p.title}?`, a: `You can request a site visit directly from this page — pick a date and time slot, or send an inquiry to the owner/broker. We'll instantly notify them on WhatsApp and email.` },
    ].filter(Boolean);

    return (
        <div className="min-h-screen bg-white" data-testid="property-detail-light">
            <Seo
                title={seoTitle}
                description={seoDesc}
                path={`/properties/${p.id}`}
                image={p.images?.[0] ? fileUrl(p.images[0]) : undefined}
                type="product"
                keywords={seoKeywords}
                jsonLd={[
                    propertyJsonLd(p),
                    breadcrumbJsonLd([
                        { name: "Home", path: "/" },
                        { name: "Properties", path: "/properties" },
                        { name: p.city || "City", path: `/properties?city=${encodeURIComponent(p.city || "")}` },
                        { name: p.title, path: `/properties/${p.id}` },
                    ]),
                    faqJsonLd(propFaqs),
                ]}
            />

            {/* Mobile: hide main Navbar + site BottomNav so our custom bars own the screen */}
            <style>{`
                @media (max-width: 767px){
                    [data-testid="main-navbar"]{ display:none !important; }
                    [data-testid="bottom-nav"]{ display:none !important; }
                    /* hide the BottomNav spacer (16px div preceding the nav) */
                    [data-testid="bottom-nav"] + *, div.md\\:hidden.h-16[aria-hidden="true"]{ display:none !important; }
                    body{ padding-bottom:0 !important; }
                }
            `}</style>

            {/* =================== MOBILE VIEW =================== */}
            <MobilePropertyView
                p={p}
                images={images}
                active={active}
                setActive={setActive}
                onOpenLightbox={() => setLightboxOpen(true)}
                onSave={onSave}
                agent={agent}
                mapSrc={mapSrc}
                descExpanded={descExpanded}
                setDescExpanded={setDescExpanded}
            />

            {lightboxOpen && (
                <ImageLightbox images={images} initial={active} title={p.title} onClose={() => setLightboxOpen(false)} />
            )}

            {/* =================== DESKTOP VIEW =================== */}
            <div className="hidden md:block">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
                <Link to="/properties" className="text-sm inline-flex items-center gap-2 mb-4 font-bold" style={{ color: "rgba(0,38,87,0.6)" }}>
                    <ArrowLeft className="w-4 h-4" /> Back to listings
                </Link>

                {/* HEADER — light card style */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-4">
                    <div>
                        <div className="inline-flex items-center gap-2 mb-2">
                            <span className="text-[10px] font-extrabold uppercase tracking-widest" style={{ color: "rgba(0,38,87,0.55)" }}>{p.property_type}{p.subtype ? ` · ${p.subtype}` : ""} · For {p.listing_type}</span>
                            {p.status === "active" && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-emerald-500/15 text-emerald-700 border border-emerald-500/30"><CheckCircle2 className="w-3 h-3" /> Available</span>}
                        </div>
                        <h1 className="font-heading text-2xl lg:text-4xl font-extrabold" style={{ color: "#002657" }}>{p.title}</h1>
                        <div className="mt-2 flex items-center gap-2 text-sm" style={{ color: "rgba(0,38,87,0.7)" }}><MapPin className="w-4 h-4" style={{ color: "#DAA520" }} /> {p.locality && `${p.locality}, `}{p.city}, {p.state}</div>
                    </div>
                    <div className="flex flex-col items-start md:items-end">
                        <div className="font-heading text-3xl lg:text-4xl font-extrabold" style={{ color: "#DAA520" }}>
                            {formatINR(p.price)}{p.listing_type === "rent" && <span className="text-base ml-1" style={{ color: "rgba(0,38,87,0.6)" }}>/month</span>}
                        </div>
                        <div className="flex gap-2 mt-3">
                            <button data-testid="btn-save" onClick={onSave} className="px-3 py-2 rounded-md border text-sm flex items-center gap-1.5 bg-white" style={{ borderColor: "rgba(0,38,87,0.15)", color: "#002657" }}>
                                <Heart className="w-4 h-4" /> Save
                            </button>
                            <PropertyShareMenu
                                propertyId={p.id}
                                propertyTitle={p.title}
                                propertyPrice={p.price}
                                propertyImage={p.images?.[0]}
                                referralEligible={p.referral_commission_eligible !== false}
                            />
                        </div>
                    </div>
                </div>

                {/* GALLERY — horizontal snap-scroll carousel with counter + lightbox */}
                <PropertyGallery images={images} title={p.title} onOpen={() => setLightboxOpen(true)} active={active} setActive={setActive} />

                {lightboxOpen && (
                    <ImageLightbox images={images} initial={active} title={p.title} onClose={() => setLightboxOpen(false)} />
                )}

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    {/* MAIN */}
                    <div className="lg:col-span-8 space-y-8">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {p.bhk != null && <Spec icon={BedDouble} label="Bedrooms" value={`${p.bhk} BHK`} />}
                            {p.bathrooms != null && <Spec icon={Bath} label="Bathrooms" value={p.bathrooms} />}
                            {p.area_sqft != null && <Spec icon={Maximize} label="Area" value={`${p.area_sqft} sqft`} />}
                            <Spec icon={ShieldCheck} label="Status" value={p.status === "active" ? "Available" : p.status} />
                            {p.facing && <Spec icon={MapPin} label="Facing" value={p.facing} />}
                            {(p.rera_id || p.approval) && <Spec icon={CheckCircle2} label="Approval" value={p.rera_id || p.approval} />}
                        </div>

                        <Section title="About this property">
                            {(() => {
                                const desc = p.description || "";
                                const isLong = desc.length > 280;
                                const shown = descExpanded || !isLong ? desc : desc.slice(0, 280).trimEnd() + "…";
                                return (
                                    <>
                                        <p className="leading-relaxed whitespace-pre-line" data-testid="property-description" style={{ color: "rgba(0,38,87,0.8)" }}>{shown}</p>
                                        {isLong && (
                                            <button type="button" onClick={() => setDescExpanded((v) => !v)} data-testid="btn-desc-toggle" className="mt-2 text-xs font-extrabold" style={{ color: "#B45309" }}>
                                                {descExpanded ? "Read Less ↑" : "Read More ↓"}
                                            </button>
                                        )}
                                    </>
                                );
                            })()}
                        </Section>

                        {p.amenities?.length > 0 && (
                            <Section title="Amenities">
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                    {p.amenities.map((a) => (
                                        <div key={a} className="flex flex-col items-center justify-center rounded-xl p-3 border text-center" style={{ backgroundColor: "#FEF9E7", borderColor: "rgba(218,165,32,0.25)" }}>
                                            <CheckCircle2 className="w-5 h-5 mb-1.5" style={{ color: "#DAA520" }} />
                                            <span className="text-xs font-bold" style={{ color: "#002657" }}>{a}</span>
                                        </div>
                                    ))}
                                </div>
                            </Section>
                        )}

                        <Section title="Location">
                            <div className="rounded-xl overflow-hidden border bg-white aspect-[16/9]" style={{ borderColor: "rgba(0,38,87,0.1)" }}>
                                <iframe title="Map" src={mapSrc} className="w-full h-full" loading="lazy" />
                            </div>
                            <div className="mt-3 text-sm" style={{ color: "rgba(0,38,87,0.7)" }}>{p.address || `${p.locality || ""}, ${p.city}, ${p.state} ${p.pincode || ""}`}</div>
                            {p.latitude != null && p.longitude != null && (
                                <div className="mt-5">
                                    <NearbyPOIs lat={p.latitude} lng={p.longitude} />
                                </div>
                            )}
                        </Section>

                        {(p.video_url || p.virtual_tour_url) && (
                            <Section title="Video & Virtual Tour">
                                <PropertyMedia videoUrl={p.video_url} virtualTourUrl={p.virtual_tour_url} />
                            </Section>
                        )}

                        <Section title="Schedule a Visit">
                            <ScheduleVisitForm property={p} ownerPhone={p.contact_phone || agent?.phone} />
                        </Section>
                    </div>

                    {/* SIDEBAR */}
                    <aside className="lg:col-span-4 space-y-5">
                        <div className="rounded-2xl bg-navy-800 border border-gold-500/20 p-6 sticky top-20">
                            {agent ? (
                                <>
                                    <div className="label-overline mb-3">Listed by {({broker:"Broker",advocate:"Advocate",loan_agent:"Loan Agent",architect:"Architect",interior_designer:"Interior Designer",civil_contractor:"Civil Contractor",seller:"Owner",user:"Owner"})[agent.role] || "Partner"}</div>
                                    <div className="flex items-center gap-4">
                                        <div className="relative">
                                            <img src={profileAvatar(agent, 128)} alt={agent.name} className="w-14 h-14 rounded-full object-cover ring-2 ring-gold-500/30" />
                                            {agent.verified && <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-gold-500 flex items-center justify-center"><ShieldCheck className="w-3 h-3 text-navy-900" strokeWidth={3} /></div>}
                                        </div>
                                        <div>
                                            <div className="font-heading font-bold text-white">{agent.name}</div>
                                            <div className="text-xs text-gold-500 uppercase tracking-wider">{agent.role.replace("_", " ")}</div>
                                            <div className="text-[11px] text-white/55 mt-0.5">{agent.experience_years || 0}+ years · ⭐ {agent.rating?.toFixed(1) || "—"}</div>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="label-overline mb-3">Listed by Owner</div>
                                    <div className="flex items-center gap-4">
                                        <div className="w-14 h-14 rounded-full bg-navy-900 ring-2 ring-gold-500/30 flex items-center justify-center text-gold-500 font-heading text-xl font-bold">
                                            {(p.owner_name || "O").charAt(0).toUpperCase()}
                                        </div>
                                        <div>
                                            <div className="font-heading font-bold text-white">{p.owner_name || "Property Owner"}</div>
                                            <div className="text-xs text-gold-500 uppercase tracking-wider">Direct Owner</div>
                                            <div className="text-[11px] text-white/55 mt-0.5">No brokerage · Deal direct</div>
                                        </div>
                                    </div>
                                </>
                            )}
                            {p.contact_phone && (
                                <a href={`tel:${p.contact_phone}`} className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md border border-gold-500/40 text-gold-500 text-sm font-semibold hover:bg-gold-500/10" data-testid="btn-call">
                                    <Phone className="w-4 h-4" /> Call {p.contact_phone}
                                </a>
                            )}
                            {p.owner_id && (
                                <ChatButton
                                    userId={p.owner_id}
                                    label={p.owner_role === "broker" ? "Chat with Broker" : "Chat with Seller"}
                                    testid="property-chat-cta"
                                    className="mt-2 w-full !bg-gold-500 !text-navy-900 !border-transparent hover:!bg-gold-400 font-extrabold"
                                />
                            )}
                            <form onSubmit={onSubmit} id="enquire-form" className="mt-5 space-y-3" data-testid="inquiry-form">
                                <div className="label-overline">Send Inquiry</div>
                                <input required value={form.name} onChange={(e) => setForm({...form, name:e.target.value})} placeholder="Your name" className="w-full bg-navy-900 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-white/40" data-testid="inquiry-name" />
                                <input required type="email" value={form.email} onChange={(e) => setForm({...form, email:e.target.value})} placeholder="Email" className="w-full bg-navy-900 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-white/40" data-testid="inquiry-email" />
                                <input required value={form.phone} onChange={(e) => setForm({...form, phone:e.target.value})} placeholder="Phone (+91)" className="w-full bg-navy-900 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-white/40" data-testid="inquiry-phone" />
                                <textarea rows={3} value={form.message} onChange={(e) => setForm({...form, message:e.target.value})} placeholder="I'm interested in this property…" className="w-full bg-navy-900 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-white/40" data-testid="inquiry-message" />
                                <button disabled={submitting} type="submit" data-testid="inquiry-submit" className="w-full px-4 py-2.5 rounded-md bg-gold-500 text-navy-900 font-semibold text-sm hover:bg-gold-100 disabled:opacity-60">
                                    {submitting ? "Sending…" : "Send Inquiry"}
                                </button>
                            </form>
                        </div>
                    </aside>
                </div>

                {/* SIMILAR PROPERTIES */}
                {similar.length > 0 && (
                    <section className="mt-14 sm:mt-20" data-testid="similar-properties">
                        <div className="flex items-end justify-between mb-6">
                            <div>
                                <div className="label-overline mb-2">You may also like</div>
                                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white">Similar properties</h2>
                            </div>
                            <Link to="/properties" className="text-sm font-semibold text-gold-500 hover:text-gold-100 hidden sm:inline">
                                Browse all →
                            </Link>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                            {similar.map((sp) => (
                                <PropertyCard key={sp.id} property={sp} />
                            ))}
                        </div>
                    </section>
                )}
            </div>
            </div>
            {/* end desktop view */}
        </div>
    );
}

/* ============================================================
   MOBILE PROPERTY VIEW — Premium native-app inspired
   Matches the referenced mobile mockup exactly.
   ============================================================ */

const AMENITY_ICON_MAP = {
    "swimming pool": { icon: "🏊", label: "Swimming Pool" },
    "pool": { icon: "🏊", label: "Pool" },
    "private pool": { icon: "🏊", label: "Private Pool" },
    "gym": { icon: "🏋️", label: "Gym" },
    "garden": { icon: "🌳", label: "Garden" },
    "park & garden": { icon: "🌳", label: "Park & Garden" },
    "parking": { icon: "🅿️", label: "Parking" },
    "valet parking": { icon: "🅿️", label: "Valet Parking" },
    "clubhouse": { icon: "🏛️", label: "Clubhouse" },
    "sea view": { icon: "🌊", label: "Sea View" },
    "concierge": { icon: "🛎️", label: "Concierge" },
    "power backup": { icon: "🔌", label: "Power Backup" },
    "24/7 security": { icon: "🛡️", label: "24/7 Security" },
    "lift": { icon: "🛗", label: "Lift" },
    "children's play area": { icon: "🧸", label: "Play Area" },
    "smart home": { icon: "🏠", label: "Smart Home" },
    "solar power": { icon: "☀️", label: "Solar" },
    "furnished": { icon: "🛋️", label: "Furnished" },
    "pet friendly": { icon: "🐾", label: "Pet Friendly" },
    "central ac": { icon: "❄️", label: "Central AC" },
    "cafeteria": { icon: "🍽️", label: "Cafeteria" },
    "clear title": { icon: "📜", label: "Clear Title" },
    "boundary wall": { icon: "🧱", label: "Boundary Wall" },
    "water connection": { icon: "💧", label: "Water" },
    "approach road": { icon: "🛣️", label: "Road" },
    "road frontage": { icon: "🛣️", label: "Road Frontage" },
    "servant quarter": { icon: "🛏️", label: "Servant Quarter" },
};

function pickAmenityIcon(a) {
    const key = String(a || "").toLowerCase().trim();
    return AMENITY_ICON_MAP[key] || { icon: "✨", label: a };
}

function buildKeyHighlights(p) {
    const list = [];
    if (p.locality) list.push("Prime Location");
    const ams = (p.amenities || []).map((a) => a.toLowerCase());
    if (ams.some((a) => a.includes("security") || a.includes("gated"))) list.push("Gated Community");
    if (ams.some((a) => a.includes("metro") || (p.address || "").toLowerCase().includes("metro"))) list.push("Near Metro Station");
    if (ams.some((a) => a.includes("garden") || a.includes("park"))) list.push("Park & Garden");
    if (p.property_type === "plot" || p.subtype === "plot") list.push("Wide Internal Roads");
    if (p.verified) list.push("RERA Verified");
    if (ams.some((a) => a.includes("clear title"))) list.push("Clear Title");
    if (p.possession_status === "ready_to_move") list.push("Ready to Move");
    list.push("Good Investment");
    return Array.from(new Set(list)).slice(0, 6);
}

function MobilePropertyView({ p, images, active, setActive, onOpenLightbox, onSave, agent, mapSrc, descExpanded, setDescExpanded }) {
    const navigate = useNavigate();
    const scrollerRef = useRef(null);
    const onScroll = () => {
        const el = scrollerRef.current;
        if (!el || !el.clientWidth) return;
        const idx = Math.round(el.scrollLeft / el.clientWidth);
        if (idx !== active) setActive(idx);
    };
    const step = (dir) => {
        const el = scrollerRef.current;
        if (!el) return;
        el.scrollBy({ left: dir * el.clientWidth, behavior: "smooth" });
    };
    const scrollToIdx = (i) => {
        const el = scrollerRef.current;
        if (el) el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
    };
    const highlights = buildKeyHighlights(p);
    const priceLabel = formatINR(p.price);
    const typeLabel = ((p.subtype || p.property_type) || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
    const listingLabel = p.listing_type === "rent" ? "For Rent" : "For Sale";
    const phone = p.contact_phone || p.owner_phone;
    const waMsg = `Hi, I'm interested in "${p.title}" listed on Global Property Mart. Please share more details.`;
    const scrollToEnquire = () => {
        const el = document.getElementById("mob-enquire-anchor");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const desc = p.description || "";
    const isLong = desc.length > 220;
    const shownDesc = descExpanded || !isLong ? desc : desc.slice(0, 220).trimEnd() + "…";

    return (
        <div className="md:hidden bg-white min-h-screen pb-24" data-testid="mob-property-detail">
            {/* Top Bar */}
            <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-100">
                <div className="flex items-center justify-between px-3 py-2.5">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        aria-label="Back"
                        data-testid="mob-back"
                        className="w-10 h-10 rounded-full flex items-center justify-center text-[#002657] active:bg-slate-100"
                    >
                        <ChevronLeft className="w-6 h-6" />
                    </button>
                    <Link to="/" className="flex items-center gap-1.5" aria-label="Home">
                        <img src="/gpm-logo.png" alt="GPM" className="w-8 h-8 object-contain" />
                        <div className="leading-tight">
                            <div className="font-heading font-extrabold text-[15px] text-[#002657] tracking-wide">GLOBAL</div>
                            <div className="text-[8px] font-bold tracking-[0.28em] text-[#DAA520] -mt-0.5">PROPERTY MART</div>
                        </div>
                    </Link>
                    <div className="flex items-center gap-1">
                        <button
                            type="button"
                            onClick={onSave}
                            aria-label="Save"
                            data-testid="mob-save-icon"
                            className="w-10 h-10 rounded-full flex items-center justify-center text-[#002657] active:bg-slate-100"
                        >
                            <Heart className="w-5 h-5" />
                        </button>
                        <PropertyShareMenu
                            propertyId={p.id}
                            propertyTitle={p.title}
                            propertyPrice={p.price}
                            propertyImage={p.images?.[0]}
                            referralEligible={p.referral_commission_eligible !== false}
                            iconOnly
                        />
                    </div>
                </div>
            </div>

            {/* Hero Image Gallery */}
            <div className="px-3 pt-3">
                <div className="relative rounded-2xl overflow-hidden bg-slate-100 aspect-[16/11]">
                    <div
                        ref={scrollerRef}
                        onScroll={onScroll}
                        className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide h-full"
                        style={{ scrollBehavior: "smooth" }}
                    >
                        {images.map((img, i) => (
                            <button
                                key={i}
                                type="button"
                                onClick={onOpenLightbox}
                                data-testid={`mob-gallery-slide-${i}`}
                                className="min-w-full h-full snap-start flex-shrink-0"
                            >
                                <img src={fileUrl(img)} alt={`${p.title} — photo ${i + 1}`} loading={i === 0 ? "eager" : "lazy"} className="w-full h-full object-cover" />
                            </button>
                        ))}
                    </div>

                    {/* Counter chip */}
                    <div className="absolute top-3 right-3 px-2.5 py-1 rounded-lg bg-black/55 backdrop-blur-sm text-white text-xs font-bold" data-testid="mob-gallery-counter">
                        {active + 1}/{images.length}
                    </div>

                    {/* Chevron arrows */}
                    {images.length > 1 && (
                        <>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); step(-1); }}
                                aria-label="Previous"
                                data-testid="mob-gallery-prev"
                                className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 shadow flex items-center justify-center text-[#002657] active:scale-95"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); step(1); }}
                                aria-label="Next"
                                data-testid="mob-gallery-next"
                                className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 shadow flex items-center justify-center text-[#002657] active:scale-95"
                            >
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </>
                    )}
                </div>

                {/* Thumbnail strip */}
                {images.length > 1 && (
                    <div className="mt-2 grid grid-cols-4 gap-2">
                        {images.slice(0, 3).map((img, i) => (
                            <button
                                key={i}
                                type="button"
                                onClick={() => scrollToIdx(i)}
                                data-testid={`mob-thumb-${i}`}
                                className={`aspect-[4/3] rounded-lg overflow-hidden border-2 transition-all ${active === i ? "border-[#DAA520]" : "border-transparent"}`}
                            >
                                <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />
                            </button>
                        ))}
                        {images.length > 3 && (
                            <button
                                type="button"
                                onClick={() => scrollToIdx(3)}
                                data-testid="mob-thumb-more"
                                className="relative aspect-[4/3] rounded-lg overflow-hidden border-2 border-transparent"
                            >
                                <img src={fileUrl(images[3])} alt="" className="w-full h-full object-cover" />
                                <div className="absolute inset-0 bg-black/55 flex items-center justify-center">
                                    <span className="text-white font-extrabold text-sm">+{images.length - 3}</span>
                                </div>
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Type + Available pill row */}
            <div className="px-4 pt-5 flex items-center justify-between gap-2">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#FDF6E0]">
                    <span className="text-[10px] font-extrabold tracking-widest uppercase text-[#B8860B]">{typeLabel} · {listingLabel}</span>
                </div>
                {p.status === "active" && (
                    <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/12 text-emerald-600 text-[11px] font-bold">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" /> Available
                    </div>
                )}
            </div>

            {/* Title & Location */}
            <div className="px-4 mt-2">
                <h1 className="font-heading text-[26px] leading-tight font-extrabold text-[#002657]" data-testid="mob-title">{p.title}</h1>
                <div className="mt-1.5 flex items-center gap-1.5 text-[13px] text-slate-600">
                    <MapPin className="w-4 h-4 text-[#DAA520] shrink-0" />
                    <span>{[p.locality, p.city, p.state].filter(Boolean).join(", ")}</span>
                </div>
            </div>

            {/* Price + Insights */}
            <div className="px-4 mt-3 flex items-center justify-between gap-3">
                <div className="font-heading text-[28px] font-extrabold text-[#DAA520] leading-none" data-testid="mob-price">
                    {priceLabel}{p.listing_type === "rent" && <span className="text-sm ml-1 text-slate-500 font-semibold">/month</span>}
                </div>
                <button
                    type="button"
                    data-testid="mob-price-insights"
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-[#FDF6E0] text-[#B8860B] text-[11px] font-extrabold border border-[#F3E4B8]"
                >
                    <TrendingUp className="w-3.5 h-3.5" /> Price Insights
                </button>
            </div>

            {/* 2x2 Spec Grid */}
            <div className="px-4 mt-4 grid grid-cols-2 gap-2.5">
                {p.area_sqft != null && <MobSpec icon={Maximize} label="Area" value={`${p.area_sqft} sqft`} testid="mob-spec-area" />}
                {(p.subtype || p.property_type) && <MobSpec icon={HomeIcon} label="Type" value={typeLabel} testid="mob-spec-type" />}
                {p.facing && <MobSpec icon={Compass} label="Facing" value={p.facing.charAt(0).toUpperCase() + p.facing.slice(1)} testid="mob-spec-facing" />}
                {(p.rera_id || p.approval) && <MobSpec icon={FileText} label="Approval" value={p.rera_id || p.approval} testid="mob-spec-approval" />}
                {p.bhk != null && <MobSpec icon={BedDouble} label="Bedrooms" value={`${p.bhk} BHK`} testid="mob-spec-bhk" />}
                {p.bathrooms != null && <MobSpec icon={Bath} label="Bathrooms" value={p.bathrooms} testid="mob-spec-bath" />}
            </div>

            {/* Key Highlights */}
            {highlights.length > 0 && (
                <div className="px-4 mt-5">
                    <div className="flex items-center gap-1.5 mb-2.5">
                        <Sparkles className="w-4 h-4 text-[#DAA520]" />
                        <h3 className="font-heading font-extrabold text-[15px] text-[#002657]">Key Highlights</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                        {highlights.map((h) => (
                            <div key={h} className="flex items-start gap-1.5">
                                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                                <span className="text-[13px] text-slate-700 leading-snug">{h}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Amenities */}
            {p.amenities?.length > 0 && (
                <div className="px-4 mt-5">
                    <div className="flex items-center justify-between mb-2.5">
                        <div className="flex items-center gap-1.5">
                            <span className="text-[16px]">👥</span>
                            <h3 className="font-heading font-extrabold text-[15px] text-[#002657]">Amenities</h3>
                        </div>
                        <ChevronRightIcon className="w-4 h-4 text-slate-400" />
                    </div>
                    <div className="flex gap-2 overflow-x-auto scrollbar-hide -mx-4 px-4">
                        {p.amenities.slice(0, 8).map((a) => {
                            const meta = pickAmenityIcon(a);
                            return (
                                <div key={a} className="shrink-0 flex flex-col items-center justify-center rounded-xl bg-slate-50 border border-slate-100 px-3 py-2.5 w-[76px]">
                                    <span className="text-xl mb-0.5">{meta.icon}</span>
                                    <span className="text-[10px] font-semibold text-[#002657] text-center leading-tight">{meta.label}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* About */}
            {desc && (
                <div className="px-4 mt-5">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1.5">
                            <FileText className="w-4 h-4 text-[#DAA520]" />
                            <h3 className="font-heading font-extrabold text-[15px] text-[#002657]">About This Property</h3>
                        </div>
                        <ChevronRightIcon className="w-4 h-4 text-slate-400" />
                    </div>
                    <p className="text-[13px] leading-relaxed text-slate-600 whitespace-pre-line" data-testid="mob-description">
                        {shownDesc}
                    </p>
                    {isLong && (
                        <button type="button" onClick={() => setDescExpanded((v) => !v)} data-testid="mob-desc-toggle" className="mt-1.5 text-[12px] font-extrabold text-[#B8860B]">
                            {descExpanded ? "Read Less ↑" : "Read More"}
                        </button>
                    )}
                </div>
            )}

            {/* Location */}
            <div className="px-4 mt-5">
                <div className="flex items-center gap-1.5 mb-2.5">
                    <MapPin className="w-4 h-4 text-[#DAA520]" />
                    <h3 className="font-heading font-extrabold text-[15px] text-[#002657]">Location</h3>
                </div>
                <div className="rounded-xl overflow-hidden border border-slate-200 aspect-[16/10]">
                    <iframe title="Map" src={mapSrc} className="w-full h-full" loading="lazy" />
                </div>
                <div className="mt-2 text-[12px] text-slate-600">{p.address || `${p.locality || ""}, ${p.city}, ${p.state} ${p.pincode || ""}`}</div>
            </div>

            {/* Schedule visit + inquiry anchor */}
            <div id="mob-enquire-anchor" className="px-4 mt-5">
                <div className="flex items-center gap-1.5 mb-2.5">
                    <Send className="w-4 h-4 text-[#DAA520]" />
                    <h3 className="font-heading font-extrabold text-[15px] text-[#002657]">Schedule a Visit</h3>
                </div>
                <ScheduleVisitForm property={p} ownerPhone={p.contact_phone || agent?.phone} />
            </div>

            {/* Sticky bottom action bar */}
            <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 shadow-[0_-8px_20px_-8px_rgba(0,38,87,0.08)]" data-testid="mob-action-bar">
                <div className="flex items-center gap-1.5 px-3 py-2.5">
                    <button
                        type="button"
                        onClick={onSave}
                        data-testid="mob-action-save"
                        className="flex flex-col items-center justify-center gap-0.5 flex-1 py-1"
                    >
                        <Heart className="w-5 h-5 text-[#002657]" />
                        <span className="text-[10px] font-bold text-slate-600">Save</span>
                    </button>
                    <div className="flex flex-col items-center justify-center gap-0.5 flex-1 py-1 relative">
                        <PropertyShareMenu
                            propertyId={p.id}
                            propertyTitle={p.title}
                            propertyPrice={p.price}
                            propertyImage={p.images?.[0]}
                            referralEligible={p.referral_commission_eligible !== false}
                            iconOnly
                        />
                        <span className="text-[10px] font-bold text-slate-600">Share</span>
                    </div>
                    {phone ? (
                        <a
                            href={`tel:${phone}`}
                            data-testid="mob-action-call"
                            className="flex flex-col items-center justify-center gap-0.5 flex-1 py-1"
                        >
                            <Phone className="w-5 h-5 text-[#002657]" />
                            <span className="text-[10px] font-bold text-slate-600">Call</span>
                        </a>
                    ) : (
                        <div className="flex-1" />
                    )}
                    {phone ? (
                        <a
                            href={`https://wa.me/91${String(phone).replace(/\D/g, "").slice(-10)}?text=${encodeURIComponent(waMsg)}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="mob-action-whatsapp"
                            className="flex flex-col items-center justify-center gap-0.5 flex-1 py-1"
                        >
                            <MessageCircle className="w-5 h-5 text-emerald-600" />
                            <span className="text-[10px] font-bold text-slate-600">WhatsApp</span>
                        </a>
                    ) : (
                        <div className="flex-1" />
                    )}
                    <button
                        type="button"
                        onClick={scrollToEnquire}
                        data-testid="mob-action-enquire"
                        className="px-6 py-3 rounded-xl font-extrabold text-white text-sm active:scale-95 transition-transform"
                        style={{ background: "linear-gradient(90deg, #DAA520 0%, #C89211 100%)" }}
                    >
                        Enquire
                    </button>
                </div>
            </div>
        </div>
    );
}

function MobSpec({ icon: Icon, label, value, testid }) {
    return (
        <div className="rounded-xl bg-white border border-slate-200 p-3 flex items-center gap-2.5" data-testid={testid}>
            <div className="w-9 h-9 rounded-lg bg-[#FDF6E0] flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4 text-[#DAA520]" />
            </div>
            <div className="min-w-0">
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">{label}</div>
                <div className="font-heading font-extrabold text-[15px] text-[#002657] leading-tight truncate">{value}</div>
            </div>
        </div>
    );
}

/* -------------------- Horizontal swipe gallery -------------------- */
function PropertyGallery({ images, title, onOpen, active, setActive }) {
    const scrollerRef = useRef(null);
    const onScroll = () => {
        const el = scrollerRef.current;
        if (!el || !el.clientWidth) return;
        const idx = Math.round(el.scrollLeft / el.clientWidth);
        if (idx !== active) setActive(idx);
    };
    const step = (dir) => {
        const el = scrollerRef.current;
        if (!el) return;
        el.scrollBy({ left: dir * el.clientWidth, behavior: "smooth" });
    };
    if (!images?.length) return null;
    return (
        <div className="mb-6" data-testid="property-gallery">
            <div className="relative rounded-2xl overflow-hidden bg-navy-800 group">
                <div
                    ref={scrollerRef}
                    onScroll={onScroll}
                    className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide aspect-[16/10] sm:aspect-[16/9]"
                    style={{ scrollBehavior: "smooth" }}
                >
                    {images.map((img, i) => (
                        <button
                            key={i}
                            type="button"
                            onClick={onOpen}
                            data-testid={`gallery-slide-${i}`}
                            className="min-w-full h-full snap-start flex-shrink-0"
                        >
                            <img src={fileUrl(img)} alt={`${title} — photo ${i + 1}`} loading={i === 0 ? "eager" : "lazy"} className="w-full h-full object-cover" />
                        </button>
                    ))}
                </div>
                {images.length > 1 && (
                    <>
                        <button type="button" onClick={() => step(-1)} data-testid="gallery-prev" aria-label="Previous" className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button type="button" onClick={() => step(1)} data-testid="gallery-next" aria-label="Next" className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <ChevronRight className="w-5 h-5" />
                        </button>
                        <div className="absolute bottom-3 right-3 px-3 py-1 rounded-full bg-black/60 text-white text-xs font-bold" data-testid="gallery-counter">
                            {active + 1} / {images.length}
                        </div>
                    </>
                )}
            </div>
            {images.length > 1 && (
                <div className="mt-2 flex gap-1.5 justify-center">
                    {images.map((_, i) => (
                        <button
                            key={i}
                            type="button"
                            onClick={() => {
                                const el = scrollerRef.current;
                                if (el) el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
                            }}
                            data-testid={`gallery-dot-${i}`}
                            aria-label={`Photo ${i + 1}`}
                            className={`h-1.5 rounded-full transition-all ${active === i ? "w-6 bg-gold-500" : "w-1.5 bg-white/30"}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

/* -------------------- Full-screen lightbox -------------------- */
function ImageLightbox({ images, initial, title, onClose }) {
    const [i, setI] = useState(initial || 0);
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowLeft") setI((v) => Math.max(0, v - 1));
            if (e.key === "ArrowRight") setI((v) => Math.min(images.length - 1, v + 1));
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [images.length, onClose]);
    return (
        <div className="fixed inset-0 z-[80] bg-black/95 flex flex-col" onClick={onClose} data-testid="lightbox">
            <div className="flex items-center justify-between p-3 text-white" onClick={(e) => e.stopPropagation()}>
                <span className="text-sm font-bold">{i + 1} / {images.length}</span>
                <button type="button" onClick={onClose} data-testid="lightbox-close" className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 flex items-center relative" onClick={(e) => e.stopPropagation()}>
                <img src={fileUrl(images[i])} alt={`${title} photo ${i + 1}`} className="w-full h-full object-contain" />
                {i > 0 && <button type="button" onClick={() => setI(i - 1)} data-testid="lightbox-prev" aria-label="Previous" className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"><ChevronLeft className="w-6 h-6" /></button>}
                {i < images.length - 1 && <button type="button" onClick={() => setI(i + 1)} data-testid="lightbox-next" aria-label="Next" className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"><ChevronRight className="w-6 h-6" /></button>}
            </div>
        </div>
    );
}

/* -------------------- Sticky mobile action bar -------------------- */
function StickyMobileBar({ property: p, onFavorite, isFav }) {
    const phone = p.contact_phone || p.owner_phone;
    if (!phone && !p.owner_id) return null;
    const enquire = () => {
        const el = document.getElementById("enquire-form");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    const waMsg = `Hi, I'm interested in "${p.title}" listed on Global Property Mart. Please share more details.`;
    return (
        <div
            className="lg:hidden fixed left-0 right-0 z-40 border-t border-white/10 backdrop-blur-md"
            style={{ bottom: "var(--mobile-bottom-nav-h, 64px)", background: "rgba(10, 18, 41, 0.92)" }}
            data-testid="sticky-mobile-bar"
        >
            <div className="grid grid-cols-4 gap-1 p-2">
                <button type="button" onClick={onFavorite} data-testid="sticky-save" className="flex flex-col items-center justify-center gap-0.5 h-12 rounded-lg bg-white/5 active:bg-white/10">
                    <Heart className={`w-4 h-4 ${isFav ? "fill-rose-500 text-rose-500" : "text-white"}`} />
                    <span className="text-[9px] font-bold text-white uppercase tracking-wide">Save</span>
                </button>
                {phone ? (
                    <a href={`tel:${phone}`} data-testid="sticky-call" className="flex flex-col items-center justify-center gap-0.5 h-12 rounded-lg bg-white/5 active:bg-white/10">
                        <Phone className="w-4 h-4 text-emerald-400" />
                        <span className="text-[9px] font-bold text-white uppercase tracking-wide">Call</span>
                    </a>
                ) : <div />}
                {phone ? (
                    <a href={`https://wa.me/91${String(phone).replace(/\D/g, "").slice(-10)}?text=${encodeURIComponent(waMsg)}`} target="_blank" rel="noreferrer" data-testid="sticky-whatsapp" className="flex flex-col items-center justify-center gap-0.5 h-12 rounded-lg bg-white/5 active:bg-white/10">
                        <MessageCircle className="w-4 h-4 text-emerald-400" />
                        <span className="text-[9px] font-bold text-white uppercase tracking-wide">WhatsApp</span>
                    </a>
                ) : <div />}
                <button type="button" onClick={enquire} data-testid="sticky-enquire" className="flex flex-col items-center justify-center gap-0.5 h-12 rounded-lg" style={{ background: "linear-gradient(90deg, #DAA520, #F59E0B)" }}>
                    <Send className="w-4 h-4" style={{ color: "#1F1300" }} />
                    <span className="text-[9px] font-extrabold uppercase tracking-wide" style={{ color: "#1F1300" }}>Enquire</span>
                </button>
            </div>
        </div>
    );
}

function Spec({ icon: Icon, label, value }) {
    return (
        <div className="rounded-xl bg-white border p-3" style={{ borderColor: "rgba(0,38,87,0.08)" }}>
            <div className="flex items-center gap-2 mb-1" style={{ color: "#DAA520" }}><Icon className="w-4 h-4" /><span className="text-[10px] font-extrabold uppercase tracking-widest">{label}</span></div>
            <div className="font-heading font-extrabold text-lg" style={{ color: "#002657" }}>{value}</div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="rounded-2xl bg-white border p-5" style={{ borderColor: "rgba(0,38,87,0.06)" }}>
            <h3 className="text-[10px] uppercase tracking-widest font-extrabold mb-3" style={{ color: "#002657" }}>{title}</h3>
            {children}
        </div>
    );
}


/* -------------------- LOGIN WALL -------------------- */
function LoginWall({ property: p }) {
    const heroImg = p.images?.[0]
        ? (typeof p.images[0] === "string" && p.images[0].startsWith("http")
            ? p.images[0]
            : fileUrl(p.images[0]))
        : "https://images.unsplash.com/photo-1638454795595-0a0abf68614d?crop=entropy&cs=srgb&fm=jpg&q=85";
    const next = encodeURIComponent(`/properties/${p.id}`);

    // AI-SEO: emit the FULL property + FAQ + breadcrumb schemas even when the login-wall is showing
    // so Google, Perplexity, ChatGPT crawlers can still index price, photos, address, etc.
    const seoAction = p.listing_type === "rent" ? "for Rent" : "for Sale";
    const seoTitle = `${p.bhk ? `${p.bhk} BHK ` : ""}${(p.subtype || p.property_type || "").replace(/_/g, " ")} ${seoAction} in ${[p.locality, p.city, p.state].filter(Boolean).join(", ")}`.trim();
    const priceLabel = `${formatINR(p.price)}${p.listing_type === "rent" ? "/month" : ""}`;
    const placeLabel = [p.locality, p.city].filter(Boolean).join(", ") || p.city || "Nagpur";
    const propFaqs = [
        { q: `What is the price of ${p.title}?`, a: `The ${p.listing_type === "rent" ? "monthly rent" : "asking price"} for ${p.title} in ${placeLabel} is ${priceLabel}.` },
        p.area_sqft && { q: `What is the area of ${p.title}?`, a: `${p.title} has a built-up / carpet area of ${p.area_sqft} sqft.` },
        p.bhk && { q: `How many bedrooms does ${p.title} have?`, a: `${p.title} is a ${p.bhk} BHK ${(p.subtype || p.property_type || "property").replace(/_/g, " ")}.` },
        { q: `Is ${p.title} RERA-verified?`, a: p.verified ? `Yes, ${p.title} is a verified listing on Global Property Mart.` : `${p.title} is listed on Global Property Mart. Contact the seller for RERA / legal documents.` },
    ].filter(Boolean);

    return (
        <div className="min-h-screen bg-navy-950 relative overflow-hidden" data-testid="property-login-wall">
            <Seo
                title={seoTitle}
                description={`${seoTitle} · ${priceLabel}${p.area_sqft ? ` · ${p.area_sqft} sqft` : ""}. ${(p.description || "").slice(0, 140)}`}
                path={`/properties/${p.id}`}
                image={heroImg}
                type="product"
                jsonLd={[
                    propertyJsonLd(p),
                    breadcrumbJsonLd([
                        { name: "Home", path: "/" },
                        { name: "Properties", path: "/properties" },
                        { name: p.city || "City", path: `/properties?city=${encodeURIComponent(p.city || "")}` },
                        { name: p.title, path: `/properties/${p.id}` },
                    ]),
                    faqJsonLd(propFaqs),
                ]}
            />
            {/* Blurred hero background */}
            <div className="absolute inset-0">
                <img src={heroImg} alt="" className="w-full h-full object-cover scale-110 blur-2xl opacity-30" />
                <div className="absolute inset-0 bg-gradient-to-b from-navy-950/70 via-navy-950/85 to-navy-950" />
            </div>

            <div className="relative max-w-4xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
                <Link
                    to="/properties"
                    data-testid="loginwall-back-link"
                    className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-gold-500 transition-colors mb-10"
                >
                    <ArrowLeft className="w-4 h-4" /> Back to listings
                </Link>

                <div className="rounded-3xl bg-navy-900/70 backdrop-blur-2xl border border-gold-500/30 shadow-[0_30px_80px_-20px_rgba(218,165,32,0.25)] overflow-hidden">
                    <div className="grid grid-cols-1 lg:grid-cols-5">
                        {/* Teaser image */}
                        <div className="lg:col-span-2 relative min-h-[220px] lg:min-h-[420px]">
                            <img src={heroImg} alt={p.title} className="absolute inset-0 w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-br from-navy-900/40 via-navy-900/30 to-navy-900/70" />
                            <div className="absolute inset-0 backdrop-blur-[6px]" />
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-20 h-20 rounded-full bg-gold-500/95 flex items-center justify-center shadow-2xl shadow-gold-500/40 ring-4 ring-white/10">
                                    <Lock className="w-9 h-9 text-navy-900" strokeWidth={2.4} />
                                </div>
                            </div>
                            <div className="absolute bottom-4 left-4 right-4">
                                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/50 backdrop-blur-sm border border-white/10 text-[10px] font-bold uppercase tracking-[0.18em] text-gold-300">
                                    <Eye className="w-3 h-3" /> Members only
                                </div>
                            </div>
                        </div>

                        {/* CTA panel */}
                        <div className="lg:col-span-3 p-8 lg:p-12">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold-500/10 border border-gold-500/30 mb-4">
                                <ShieldCheck className="w-3.5 h-3.5 text-gold-400" />
                                <span className="text-[10px] font-bold tracking-[0.18em] uppercase text-gold-300">Verified Listing</span>
                            </div>
                            <h1 className="font-heading text-3xl lg:text-4xl font-bold text-white tracking-tight leading-tight" data-testid="loginwall-title">
                                {p.title}
                            </h1>
                            <div className="flex items-center gap-2 text-sm text-white/70 mt-2">
                                <MapPin className="w-4 h-4 text-gold-500" />
                                <span>{[p.locality, p.city].filter(Boolean).join(", ") || p.city}</span>
                            </div>

                            <div className="mt-6 rounded-xl bg-white/5 border border-white/10 p-4">
                                <p className="text-sm text-white/80 leading-relaxed">
                                    <strong className="text-gold-400 font-semibold">Sign up or log in</strong> to view property price, photos, owner contact and full details. It&apos;s free and takes less than 30 seconds.
                                </p>
                            </div>

                            <ul className="mt-6 space-y-2.5 text-sm text-white/75">
                                {[
                                    "View full property price & specifications",
                                    "Access verified owner / broker contact",
                                    "Save properties & get instant alerts",
                                    "Earn ₹200 signup bonus on registration",
                                ].map((b) => (
                                    <li key={b} className="flex items-start gap-2.5">
                                        <CheckCircle2 className="w-4 h-4 text-gold-500 mt-0.5 shrink-0" />
                                        <span>{b}</span>
                                    </li>
                                ))}
                            </ul>

                            <div className="mt-8 flex flex-col sm:flex-row gap-3">
                                <Link
                                    to={`/register?next=${next}`}
                                    data-testid="loginwall-register"
                                    className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gold-500 text-navy-900 font-bold text-sm tracking-wide hover:bg-gold-100 transition-colors shadow-lg shadow-gold-500/20"
                                >
                                    <UserPlus className="w-4 h-4" /> Sign up free
                                </Link>
                                <Link
                                    to={`/login?next=${next}`}
                                    data-testid="loginwall-login"
                                    className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-white/10 border border-white/20 text-white font-semibold text-sm hover:bg-white/15 transition-colors"
                                >
                                    <LogIn className="w-4 h-4" /> Log in
                                </Link>
                            </div>

                            <p className="text-[11px] text-white/45 mt-5 text-center">
                                By continuing you agree to our Terms & Privacy Policy.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
